import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { useCampaignActions } from '../hooks/useCampaignActions';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import type { CampaignDetail } from '../outreachTypes';
import { QuestionPanel } from './QuestionPanel';
import { planRate } from './planRates';
import { startMessages as messages } from './startMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Builder.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * "How pitches go out" (design outreach-v2 f1): review each pitch, or autopilot (Label only). On
 * another plan the autopilot card is locked and its link opens the Label offer.
 */
const ModeCardsComponent: React.FC<{ accountId: number; campaign: CampaignDetail; readOnly: boolean }> = ({
  accountId,
  campaign,
  readOnly,
}) => {
  const intl = useIntl();
  const plan = useOutreachPlan();
  const openUpgradePrompt = useUpgradePrompt();
  const { updateCampaign } = useCampaignActions(accountId);
  const isAutopilot = campaign.mode === 'autopilot' && plan.canUseAutopilot;
  const setMode = (mode: 'review' | 'autopilot') =>
    updateCampaign({ campaignUuid: campaign.campaignUuid, mode }).catch(console.error);

  return (
    <QuestionPanel title={intl.formatMessage(messages.modeTitle)} testId="builder-mode">
      <div className={styles.modes}>
        <button
          type="button"
          className={clsx(styles.pickable, styles.mode, !isAutopilot && styles.picked)}
          aria-pressed={!isAutopilot}
          disabled={readOnly}
          onClick={() => setMode('review')}
        >
          <span className={styles.modeHead}>
            <span className={styles.radio} />
            <b>{intl.formatMessage(messages.modeReview)}</b>
          </span>
          <small>{intl.formatMessage(messages.modeReviewSub, { rate: campaign.dailyRate })}</small>
        </button>
        {plan.canUseAutopilot ? (
          <button
            type="button"
            className={clsx(styles.pickable, styles.mode, isAutopilot && styles.picked)}
            aria-pressed={isAutopilot}
            disabled={readOnly}
            onClick={() => setMode('autopilot')}
          >
            <span className={styles.modeHead}>
              <span className={styles.radio} />
              <b>{intl.formatMessage(messages.modeAutopilot)}</b>
            </span>
            <small>{intl.formatMessage(messages.modeAutopilotSub)}</small>
          </button>
        ) : (
          <div className={clsx(styles.pickable, styles.mode, styles.modeLocked)} data-testid="builder-mode-locked">
            <span className={styles.modeHead}>
              <FontAwesomeIcon icon={faLock} />
              <b>{intl.formatMessage(messages.modeAutopilot)}</b>
              <span className={clsx(shared.chip, shared.chipVio)}>{intl.formatMessage(messages.modeLabelChip)}</span>
            </span>
            <small>{intl.formatMessage(messages.modeAutopilotSub)}</small>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-start"
              disabled={plan.isDemo}
              onClick={() => openUpgradePrompt(SMLUpgradeFeature.outreachRate, campaign.songTitle)}
            >
              {intl.formatMessage(messages.modeAutopilotOffer, { label: planRate('label') })}
            </button>
          </div>
        )}
      </div>
    </QuestionPanel>
  );
};

export const ModeCards = TypedMemo(ModeCardsComponent);
